import {Trans} from '@lingui/macro';
import {first} from 'lodash';
import React, {FC, useCallback, useEffect, useState} from 'react';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isActivityInConversationRelevant} from '../../../../helpers/models/conversationHelpers';
import {ActivityModel} from '../../../../models/api/activityModel';
import {UnreadBannerBase, UnreadBannerPositionsEnum} from './unreadBannerBase';

/*
 * Props.
 */

interface UnreadBannerBelowProps {
  onUnreadBannerClick: HandlerOf<number | undefined>;
  activities: ReadonlyArray<ActivityModel>;
  hasUnreadActivityBelow: boolean;
  lastActivity?: ActivityModel;
  lastVisibleActivityDate: number;
  originalLastActivityDate: number;
  isAllActivitiesLoadedBelow: boolean;
}

/*
 * Component.
 */

export const UnreadBannerBelow: FC<UnreadBannerBelowProps> = (props) => {
  const [newUnreadActivities, setNewUnreadActivities] = useState<ReadonlyArray<ActivityModel>>([]);
  const [isUnreadBelowDismissed, setIsUnreadBelowDismissed] = useState(false);

  const {
    activities,
    hasUnreadActivityBelow,
    lastActivity,
    onUnreadBannerClick,
    lastVisibleActivityDate,
    originalLastActivityDate,
    isAllActivitiesLoadedBelow,
  } = props;

  // Check for total new unread activities while on the conversation
  useEffect(() => {
    // No new unread if we don't have any activity or we've seen the page after the last activity was created
    if (!lastActivity || originalLastActivityDate >= lastActivity.date) {
      return;
    }

    // No new unread if our last visible activity is more recent than (or equal to in most cases) the last activity
    if (lastVisibleActivityDate >= lastActivity.date) {
      setNewUnreadActivities([]);
      return;
    }

    // Find unread activities that were received after the page loaded below the visible view
    const unreadActivities = activities.filter(
      (activity) => activity.date > lastVisibleActivityDate && isActivityInConversationRelevant(activity),
    );

    setNewUnreadActivities(unreadActivities);
  }, [activities, lastActivity, lastVisibleActivityDate, originalLastActivityDate]);

  const onDismissBanner = useCallback(() => {
    setIsUnreadBelowDismissed(true);
    setNewUnreadActivities([]);
  }, []);

  const onNavigateToUnread = useCallback(() => {
    const firstNewUnreadActivity = first(newUnreadActivities);
    const firstNewUnreadActivityId = firstNewUnreadActivity && firstNewUnreadActivity.id;

    // If we have new unseen messages that were received while we were on the conversation go to the first one
    if (firstNewUnreadActivityId) {
      onUnreadBannerClick(firstNewUnreadActivityId);
    }
    // If we have the last activity loaded and loaded in memory, navigate to that activity
    else if (lastActivity && isAllActivitiesLoadedBelow) {
      onUnreadBannerClick(lastActivity.id);
    }
    // Jump to the most recent activity in the conversation.
    else {
      onUnreadBannerClick(undefined);
    }

    onDismissBanner();
  }, [newUnreadActivities, onUnreadBannerClick, lastActivity, onDismissBanner, isAllActivitiesLoadedBelow]);

  const hasUnreadBelow = !isUnreadBelowDismissed && hasUnreadActivityBelow;
  const hasNewUnreadActivity = newUnreadActivities.length > 0;
  const isVisible = hasUnreadBelow || hasNewUnreadActivity;
  if (!isVisible) {
    return null;
  }

  return (
    <UnreadBannerBase
      currentPosition={UnreadBannerPositionsEnum.BOTTOM}
      label={renderUnreadBannerLabel(newUnreadActivities.length)}
      onNavigateToUnreadClick={onNavigateToUnread}
      onDismissBannerClick={onDismissBanner}
    />
  );
};

/*
 * Helpers.
 */

function renderUnreadBannerLabel(newUnreadCount: number) {
  if (newUnreadCount) {
    return <Trans id="unread-banner-below-newestunread">{newUnreadCount} unread</Trans>;
  }

  return <Trans id="unread-banner-below-unread">Jump to most recent</Trans>;
}
